import { useEffect, useMemo, useState } from 'react';

const SEP = '\u0001';

/**
 * Tracks which heading id is “current” while scrolling (last heading above offset).
 * `offsetPx` should clear the fixed header (e.g. 96).
 */
export function useActiveTocSection(
  ids: readonly string[],
  offsetPx: number
): string | null {
  const [activeId, setActiveId] = useState<string | null>(null);
  const idsKey = useMemo(
    () => (ids.length > 0 ? ids.join(SEP) : ''),
    [ids]
  );

  useEffect(() => {
    const idList = idsKey.length > 0 ? idsKey.split(SEP) : [];
    if (idList.length === 0) {
      setActiveId(null);
      return;
    }

    const update = () => {
      let current: string | null = null;
      for (const id of idList) {
        const el = document.getElementById(id);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top <= offsetPx) {
          current = id;
        }
      }
      setActiveId(current ?? idList[0]);
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [idsKey, offsetPx]);

  return activeId;
}
